import { useEffect, useState, useCallback } from 'react';
import { api } from '@/api';
import { format, subDays } from 'date-fns';

type Tab = 'preview' | 'contacts' | 'milestones' | 'invoices' | 'issues' | 'schedule';

interface FieldDef {
  key: string;
  label: string;
  type?: string;
  dbKey?: string; // key as returned from API (snake_case)
  width?: string;
}

const TAB_FIELDS: Record<Exclude<Tab, 'preview'>, FieldDef[]> = {
  contacts: [
    { key: 'name', dbKey: 'name', label: 'Name' },
    { key: 'organization', dbKey: 'organization', label: 'Organization' },
    { key: 'role', dbKey: 'role', label: 'Role' },
    { key: 'email', dbKey: 'email', label: 'Email' },
  ],
  milestones: [
    { key: 'name', dbKey: 'name', label: 'Milestone' },
    { key: 'paymentAmount', dbKey: 'payment_amount', label: 'Amount', type: 'number' },
  ],
  invoices: [
    { key: 'invoiceDate', dbKey: 'invoice_date', label: 'Date', type: 'date' },
    { key: 'invoiceNo', dbKey: 'invoice_no', label: 'Invoice No.' },
    { key: 'activity', dbKey: 'activity', label: 'Activity' },
    { key: 'amount', dbKey: 'amount', label: 'Amount', type: 'number' },
  ],
  issues: [
    { key: 'issue', dbKey: 'issue', label: 'Issue/Concern' },
    { key: 'assignedTo', dbKey: 'assigned_to', label: 'Assigned To' },
    { key: 'dateIdentified', dbKey: 'date_identified', label: 'Identified', type: 'date' },
    { key: 'dateResolved', dbKey: 'date_resolved', label: 'Resolved', type: 'date' },
    { key: 'comments', dbKey: 'comments', label: 'Comments' },
  ],
  schedule: [
    { key: 'milestone', dbKey: 'milestone', label: 'Milestone' },
    { key: 'responsible', dbKey: 'responsible', label: 'Responsible' },
    { key: 'deadline', dbKey: 'deadline', label: 'Deadline', type: 'date' },
    { key: 'actualDate', dbKey: 'actual_date', label: 'Actual', type: 'date' },
    { key: 'status', dbKey: 'status', label: 'Status' },
  ],
};

// Tabs that support reorder (have sort_order column)
const REORDERABLE_TABS: Tab[] = ['contacts', 'milestones', 'schedule'];

export default function AdminForceAccountPage() {
  const [activeTab, setActiveTab] = useState<Tab>('preview');
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 14), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [reportHtml, setReportHtml] = useState('');
  const [reportLoading, setReportLoading] = useState(false);
  const [items, setItems] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  // Add form
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState<Record<string, string>>({});

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editData, setEditData] = useState<Record<string, string>>({});

  const loadReportHtml = useCallback(async () => {
    setReportLoading(true);
    try {
      const base = import.meta.env.PROD
        ? 'https://ultisim-teams-api.ultisim.workers.dev/api/v1'
        : '/api/v1';
      const accessToken = localStorage.getItem('accessToken');
      const resp = await fetch(
        `${base}/reports/force-account/html?startDate=${startDate}&endDate=${endDate}`,
        {
          headers: accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {},
          credentials: 'include',
        }
      );
      if (resp.ok) {
        setReportHtml(await resp.text());
      } else {
        setReportHtml(`<html><body style="font-family:sans-serif;padding:40px;color:#666"><h2>Failed to load report</h2><p>Status: ${resp.status}</p></body></html>`);
      }
    } catch (err) {
      console.error('Failed to load report:', err);
      setReportHtml('<html><body style="font-family:sans-serif;padding:40px;color:#666"><h2>Failed to load report</h2></body></html>');
    } finally {
      setReportLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (activeTab === 'preview') {
      loadReportHtml();
    } else {
      loadTabData();
    }
    setEditingId(null);
    setShowAddForm(false);
    setFormData({});
  }, [activeTab, startDate, endDate]);

  useEffect(() => {
    if (successMsg) {
      const timer = setTimeout(() => setSuccessMsg(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMsg]);

  const loadTabData = async () => {
    setIsLoading(true);
    try {
      const data = await api.get<any[]>(`/reports/force-account/${activeTab}`);
      setItems(data);
    } catch (err) {
      console.error('Failed to load:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // --- CRUD ---
  const addItem = async () => {
    try {
      await api.post(`/reports/force-account/${activeTab}`, formData);
      setShowAddForm(false);
      setFormData({});
      setSuccessMsg('Added successfully');
      await loadTabData();
    } catch (err) {
      console.error('Failed to add:', err);
    }
  };

  const saveEdit = async (id: string) => {
    try {
      await api.put(`/reports/force-account/${activeTab}/${id}`, editData);
      setEditingId(null);
      setEditData({});
      setSuccessMsg('Updated successfully');
      await loadTabData();
    } catch (err) {
      console.error('Failed to update:', err);
    }
  };

  const deleteItem = async (id: string) => {
    try {
      await api.delete(`/reports/force-account/${activeTab}/${id}`);
      setSuccessMsg('Deleted');
      await loadTabData();
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const startEdit = (item: any) => {
    const fields = TAB_FIELDS[activeTab as Exclude<Tab, 'preview'>];
    const data: Record<string, string> = {};
    for (const f of fields) {
      data[f.key] = item[f.dbKey!] != null ? String(item[f.dbKey!]) : '';
    }
    setEditingId(item.id);
    setEditData(data);
  };

  // --- Reorder ---
  const moveItem = async (index: number, direction: 'up' | 'down') => {
    const newItems = [...items];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= newItems.length) return;
    [newItems[index], newItems[swapIndex]] = [newItems[swapIndex], newItems[index]];
    setItems(newItems);
    // Persist
    try {
      await api.post(`/reports/force-account/${activeTab}/reorder`, { ids: newItems.map((i: any) => i.id) });
    } catch (err) {
      console.error('Reorder failed:', err);
      await loadTabData(); // revert
    }
  };

  // --- Special actions ---
  const syncScheduleFromDevOps = async () => {
    setIsLoading(true);
    try {
      const result = await api.post<{ synced: number; message: string }>('/reports/force-account/sync-schedule', {});
      setSuccessMsg(result.message);
      await loadTabData();
    } catch (err: any) {
      setSuccessMsg(`Sync failed: ${err?.message || 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const seedMilestones = async () => {
    setIsLoading(true);
    try {
      const result = await api.post<{ seeded: number; message: string }>('/reports/force-account/seed-milestones', {});
      setSuccessMsg(result.message);
      await loadTabData();
    } catch (err: any) {
      setSuccessMsg(`Seed failed: ${err?.message || 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const getReportUrl = () => {
    const base = import.meta.env.PROD
      ? 'https://ultisim-teams-api.ultisim.workers.dev/api/v1'
      : '/api/v1';
    return `${base}/reports/force-account/html?startDate=${startDate}&endDate=${endDate}`;
  };

  const tabs: { key: Tab; label: string }[] = [
    { key: 'preview', label: 'Report Preview' },
    { key: 'contacts', label: 'Contacts' },
    { key: 'milestones', label: 'Milestones' },
    { key: 'invoices', label: 'Invoices' },
    { key: 'issues', label: 'Issues' },
    { key: 'schedule', label: 'Schedule' },
  ];

  const canReorder = REORDERABLE_TABS.includes(activeTab);

  const formatCellValue = (key: string, value: any) => {
    if ((key === 'amount' || key === 'payment_amount') && value != null) {
      return `$${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
    }
    return value || '—';
  };

  // --- Render helpers ---
  const renderAddForm = () => {
    const fields = TAB_FIELDS[activeTab as Exclude<Tab, 'preview'>];
    if (!fields) return null;

    return (
      <div className="card animate-slide-down mb-4">
        <h3 className="text-lg font-medium text-white mb-4">Add New</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {fields.map(f => (
            <div key={f.key}>
              <label className="block text-sm text-dark-400 mb-1">{f.label}</label>
              <input
                type={f.type || 'text'}
                step={f.type === 'number' ? '0.01' : undefined}
                value={formData[f.key] || ''}
                onChange={(e) => setFormData({ ...formData, [f.key]: e.target.value })}
                className="input w-full"
              />
            </div>
          ))}
        </div>
        <div className="flex gap-3">
          <button onClick={addItem} className="btn-primary">Add</button>
          <button onClick={() => { setShowAddForm(false); setFormData({}); }} className="btn-ghost">Cancel</button>
        </div>
      </div>
    );
  };

  const renderTable = () => {
    if (isLoading) return <div className="flex justify-center py-8"><div className="spinner w-8 h-8" /></div>;

    const fields = TAB_FIELDS[activeTab as Exclude<Tab, 'preview'>];
    if (!fields) return null;

    return (
      <div className="overflow-x-auto">
        <table className="table">
          <thead>
            <tr>
              {canReorder && <th className="w-16">Order</th>}
              {fields.map(f => <th key={f.key}>{f.label}</th>)}
              <th className="text-right w-40">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={fields.length + (canReorder ? 2 : 1)} className="text-center text-dark-500 py-8">No items</td></tr>
            ) : (
              items.map((item: any, idx: number) => (
                <tr key={item.id}>
                  {/* Reorder buttons */}
                  {canReorder && (
                    <td className="px-2">
                      <div className="flex flex-col gap-0.5">
                        <button
                          onClick={() => moveItem(idx, 'up')}
                          disabled={idx === 0}
                          className="text-dark-500 hover:text-white disabled:opacity-20 text-xs leading-none"
                          title="Move up"
                        >▲</button>
                        <button
                          onClick={() => moveItem(idx, 'down')}
                          disabled={idx === items.length - 1}
                          className="text-dark-500 hover:text-white disabled:opacity-20 text-xs leading-none"
                          title="Move down"
                        >▼</button>
                      </div>
                    </td>
                  )}

                  {/* Data cells - inline edit or display */}
                  {editingId === item.id ? (
                    fields.map(f => (
                      <td key={f.key} className="px-3 py-1">
                        <input
                          type={f.type || 'text'}
                          step={f.type === 'number' ? '0.01' : undefined}
                          value={editData[f.key] || ''}
                          onChange={(e) => setEditData({ ...editData, [f.key]: e.target.value })}
                          className="input text-sm py-1 px-2 w-full"
                        />
                      </td>
                    ))
                  ) : (
                    fields.map(f => (
                      <td key={f.key} className="text-dark-300">
                        {formatCellValue(f.dbKey!, item[f.dbKey!])}
                      </td>
                    ))
                  )}

                  {/* Action buttons */}
                  <td className="text-right">
                    {editingId === item.id ? (
                      <div className="flex justify-end gap-2">
                        <button onClick={() => saveEdit(item.id)} className="btn-sm btn-primary">Save</button>
                        <button onClick={() => { setEditingId(null); setEditData({}); }} className="btn-sm btn-ghost">Cancel</button>
                      </div>
                    ) : (
                      <div className="flex justify-end gap-2">
                        <button onClick={() => startEdit(item)} className="btn-sm bg-dark-600 hover:bg-dark-500 text-dark-200 rounded-lg px-3 py-1 text-sm transition-colors">Edit</button>
                        <button onClick={() => deleteItem(item.id)} className="btn-sm btn-danger">Delete</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-white">Force Account Report</h1>
        <p className="text-dark-400 mt-1">Auto-generated Tetra Tech progress report</p>
      </div>

      {successMsg && (
        <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg text-green-400 text-sm">
          {successMsg}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-dark-700 overflow-x-auto">
        <div className="flex gap-4">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.key
                  ? 'border-primary-500 text-primary-400'
                  : 'border-transparent text-dark-400 hover:text-dark-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Preview tab */}
      {activeTab === 'preview' && (
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <div>
              <label className="block text-sm text-dark-400 mb-1">Start Date</label>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="input" />
            </div>
            <div>
              <label className="block text-sm text-dark-400 mb-1">End Date</label>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="input" />
            </div>
            <div className="self-end flex gap-2">
              <button onClick={loadReportHtml} disabled={reportLoading} className="btn-ghost">
                {reportLoading ? 'Loading...' : 'Refresh'}
              </button>
              <a href={getReportUrl()} target="_blank" rel="noopener noreferrer" className="btn-primary inline-block">
                Open Full Report
              </a>
            </div>
          </div>
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            {reportLoading ? (
              <div className="flex justify-center items-center" style={{ height: '800px' }}>
                <div className="spinner w-8 h-8" />
              </div>
            ) : (
              <iframe
                srcDoc={reportHtml}
                className="w-full border-0"
                style={{ height: '800px', background: '#ffffff' }}
                title="Force Account Report Preview"
              />
            )}
          </div>
        </div>
      )}

      {/* Data tabs */}
      {activeTab !== 'preview' && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white capitalize">{activeTab}</h2>
            <div className="flex gap-2">
              {activeTab === 'schedule' && (
                <button onClick={syncScheduleFromDevOps} disabled={isLoading} className="btn-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-3 py-1.5 text-sm transition-colors disabled:opacity-50">
                  {isLoading ? 'Syncing...' : 'Sync from DevOps'}
                </button>
              )}
              {activeTab === 'milestones' && items.length === 0 && (
                <button onClick={seedMilestones} disabled={isLoading} className="btn-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-3 py-1.5 text-sm transition-colors disabled:opacity-50">
                  {isLoading ? 'Seeding...' : 'Seed from SOW'}
                </button>
              )}
              <button onClick={() => { setShowAddForm(!showAddForm); setEditingId(null); }} className="btn-primary btn-sm">
                {showAddForm ? 'Cancel' : '+ Add'}
              </button>
            </div>
          </div>

          {showAddForm && renderAddForm()}
          {renderTable()}
        </div>
      )}
    </div>
  );
}
